import type React from 'react';
import Tooltip from '@/components/Tooltip';

/**
 * Format a key for display in the tooltip
 */
const formatKeyDisplay = (key: string): string => {
  const keyMappings: Record<string, string> = {
    ' ': 'Space',
    ArrowUp: '↑',
    ArrowDown: '↓',
    ArrowLeft: '←',
    ArrowRight: '→',
    ',': ',',
    '.': '.',
    shift: 'Shift',
    ctrl: 'Ctrl',
    alt: 'Alt',
    meta: 'Cmd'
  };
  return keyMappings[key] || key;
};
export interface KeyboardShortcutsTooltipProps {
  /** Tooltip alignment */
  align?: 'start' | 'center' | 'end';
  /** The element to wrap with the tooltip */
  children: React.ReactNode;
  /** Delay before showing tooltip in milliseconds */
  delayDuration?: number;
  /** Whether to show the tooltip */
  disabled?: boolean;
  /** Array of keys to display (e.g., ['Shift', 'N'] or ['Space']) */
  keys: string[];
  /** The tooltip label text */
  label: React.ReactNode;
  /** Tooltip placement side */
  side?: 'top' | 'bottom' | 'left' | 'right';
}

/**
 * Tooltip component that displays keyboard shortcuts in a formatted way
 *
 * @example
 * ```tsx
 * <KeyboardShortcutsTooltip label="Play/Pause" keys={['Space']}>
 *   <Button>Play</Button>
 * </KeyboardShortcutsTooltip>
 *
 * <KeyboardShortcutsTooltip label="Next track" keys={['Shift', 'N']}>
 *   <Button>Next</Button>
 * </KeyboardShortcutsTooltip>
 * ```
 */
export const KeyboardShortcutsTooltip = ({
  label,
  keys,
  children,
  side = 'top',
  align = 'center',
  delayDuration = 800,
  disabled = false
}: KeyboardShortcutsTooltipProps) => {
  if (disabled || keys.length === 0) {
    return <>{children}</>;
  }
  return <Tooltip align={align} delayDuration={delayDuration} label={<div className="flex items-center gap-1 whitespace-nowrap">
          <span className="pr-1 last:pr-0">{label}</span>
          {keys.map(key => <span className="rounded border border-gray-150 border-solid bg-clay-100 px-1 py-0.5 pt-1 font-sans text-sm leading-none dark:border-gray-700 dark:bg-gray-800" key={key}>
              {formatKeyDisplay(key)}
            </span>)}
        </div>} side={side} data-sentry-element="Tooltip" data-sentry-component="KeyboardShortcutsTooltip" data-sentry-source-file="KeyboardShortcutsTooltip.tsx">
      {children}
    </Tooltip>;
};